import {
  LogTemplateType,
  TemplateTypeEnum,
  useAllWorklogsTemplatesForKpiTrackerLazyQuery,
  useAssemblyTemplatesLazyQuery,
  LogsTemplate,
} from '@/__generated__/graphql';
import * as kpiState from '@/modules/KpiTracker/state';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

type Props = {
  structureIds: string[];
};

export const useFetchWorklogTemplatesEffect = ({ structureIds }: Props) => {
  const [fetchWorklogTemplates] = useAllWorklogsTemplatesForKpiTrackerLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const [fetchAssemblyTemplates] = useAssemblyTemplatesLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const setSelectedWorklogsTemplate = useSetAtom(kpiState.selectedWorklogsTemplate);
  const setSelectedIndirectPobLogsTemplateForKpi = useSetAtom(
    kpiState.selectedIndirectPobLogsTemplateForKpi
  );
  const setSelectedCostLogsTemplateForKpi = useSetAtom(kpiState.selectedCostLogsTemplateForKpi);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        if (!Array.isArray(structureIds) || structureIds.length === 0) return;

        const firstStructureId = structureIds[0];
        const secondStructureId = structureIds?.[1];

        const firstTemplateId = await (async () => {
          try {
            const firstAssemblyTemplates = await fetchAssemblyTemplates({
              variables: {
                structureId: firstStructureId,
                type: TemplateTypeEnum.Assembly,
              },
            });

            return firstAssemblyTemplates?.data?.allTemplates?.at(0)?.id;
          } catch {
            return undefined;
          }
        })();

        const fetchLogsTemplate = async (structureId: string, type: LogTemplateType) => {
          const response = await fetchWorklogTemplates({
            variables: {
              input: {
                structureId,
                type,
              },
            },
          });
          return response?.data?.allLogsTemplates?.at(0);
        };

        const firstWorkpack = await (async () => {
          if (!firstTemplateId) return;
          return fetchLogsTemplate(firstStructureId, LogTemplateType.WorklogTemplate);
        })();

        if (firstWorkpack) {
          setSelectedWorklogsTemplate(firstWorkpack as LogsTemplate);
          const [firstIndirect, firstCost] = await Promise.all([
            fetchLogsTemplate(firstStructureId, LogTemplateType.IndirectPobTemplate),
            fetchLogsTemplate(firstStructureId, LogTemplateType.CostLogTemplate),
          ]);
          setSelectedIndirectPobLogsTemplateForKpi(
            firstIndirect ? (firstIndirect as LogsTemplate) : undefined
          );
          setSelectedCostLogsTemplateForKpi(firstCost ? (firstCost as LogsTemplate) : undefined);
          return;
        }

        const secondTemplateId = await (async () => {
          try {
            const secondAssemblyTemplates = await fetchAssemblyTemplates({
              variables: {
                structureId: secondStructureId,
                type: TemplateTypeEnum.Assembly,
              },
            });

            return secondAssemblyTemplates?.data?.allTemplates?.at(0)?.id || firstTemplateId;
          } catch {
            return undefined;
          }
        })();

        if (!secondTemplateId) return;

        const secondWorkpack = secondStructureId
          ? await fetchLogsTemplate(secondStructureId, LogTemplateType.WorklogTemplate)
          : undefined;

        if (secondWorkpack && secondStructureId) {
          setSelectedWorklogsTemplate(secondWorkpack as LogsTemplate);
          const [secondIndirect, secondCost] = await Promise.all([
            fetchLogsTemplate(secondStructureId, LogTemplateType.IndirectPobTemplate),
            fetchLogsTemplate(secondStructureId, LogTemplateType.CostLogTemplate),
          ]);
          setSelectedIndirectPobLogsTemplateForKpi(
            secondIndirect ? (secondIndirect as LogsTemplate) : undefined
          );
          setSelectedCostLogsTemplateForKpi(secondCost ? (secondCost as LogsTemplate) : undefined);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetch().then();
  }, [
    fetchAssemblyTemplates,
    fetchWorklogTemplates,
    setSelectedIndirectPobLogsTemplateForKpi,
    setSelectedCostLogsTemplateForKpi,
    setSelectedWorklogsTemplate,
    structureIds,
  ]);

  return { loading };
};
